<script setup lang="ts">
  /**
   * Save bar — 03-components/23-sticky-bar · OSCloud DS 1.1.152
   *
   * The save-state form of the card: a status line (dot + copy) on the left, the actions on the
   * right. State is read off `data-state` / `data-docked`, never a modifier class; `idle` carries
   * no `data-state` at all. `live` is the card's `--live`: a bar that is the page's only way out
   * keeps its orange edge from first paint, and in `idle` the dot stays and reports "Nothing to
   * save" instead of going blank.
   *
   * `summary` switches the bar to its selection form (DS 1.1.120): what a picker has chosen,
   * title over detail on the left, price flush against the one action. One voice per bar — the
   * summary replaces the status line, and it is live by being present, so it takes no `state`.
   */
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import type { SaveStatusState } from '@/composable/useSaveStatus'
  import type { BfSaveBarSummary } from './BfSaveBarTypes'

  const props = withDefaults(
    defineProps<{
      state?: SaveStatusState
      /** The page's only way out — the edge is permanent, the dot and status report the state. */
      live?: boolean
      dirtyText?: string
      savedText?: string
      /** Shown in `idle` only when `live`; a plain bar has nothing to report while untouched. */
      idleText?: string
      ariaLabel?: string
      /** The selection form: replaces the status line with what the picker has chosen. */
      summary?: BfSaveBarSummary | null
    }>(),
    {
      state: 'idle',
      live: false,
      dirtyText: 'You have unsaved changes.',
      savedText: 'All changes saved.',
      idleText: 'Nothing to save',
      ariaLabel: 'Actions',
      summary: null,
    },
  )

  const statusText = computed(() => {
    if (props.state === 'dirty') return props.dirtyText
    if (props.state === 'saved') return props.savedText
    return props.live ? props.idleText : ''
  })

  // Docking is measured, not observed: the bar is docked while its bottom edge sits within 1px of
  // the viewport's. An IntersectionObserver's negative rootMargin is ignored wherever the root
  // bounds are unknown, and the app scrolls inside a container, so the card reads the rect instead.
  // Scroll is heard in capture so a container scroll counts; the interval catches layout shifts no
  // event reports (a sibling collapsing, a font landing, an async render settling).
  const root = ref<HTMLElement | null>(null)
  const isDocked = ref(false)
  let timer: ReturnType<typeof setTimeout> | undefined
  let interval: ReturnType<typeof setInterval> | undefined

  const measure = () => {
    timer = undefined
    if (!root.value) return
    isDocked.value = root.value.getBoundingClientRect().bottom >= window.innerHeight - 1
  }

  const schedule = () => {
    if (!timer) timer = setTimeout(measure, 50)
  }

  onMounted(() => {
    window.addEventListener('scroll', schedule, { capture: true, passive: true })
    window.addEventListener('resize', schedule)
    interval = setInterval(schedule, 500)
    schedule()
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', schedule, { capture: true })
    window.removeEventListener('resize', schedule)
    clearInterval(interval)
    clearTimeout(timer)
  })
</script>

<template>
  <div
    ref="root"
    class="save-bar"
    :class="{ 'save-bar--live': live }"
    :data-state="state === 'idle' ? undefined : state"
    :data-docked="isDocked || undefined"
    :aria-label="ariaLabel"
  >
    <div class="save-bar__info">
      <!-- A <p> live region, so a new choice is announced as one sentence. -->
      <p v-if="summary" class="save-bar__summary" role="status" aria-live="polite">
        <span class="save-bar__summary-title">{{ summary.title }}</span>
        <span v-if="summary.detail" class="save-bar__summary-detail" data-compact="hide">{{ summary.detail }}</span>
        <span v-if="summary.amount" class="save-bar__summary-price">
          <span v-if="summary.currency" class="save-bar__summary-currency">{{ summary.currency }}</span>
          <span class="save-bar__summary-amount">{{ summary.amount }}</span>
          <span v-if="summary.period" class="save-bar__summary-period">{{ summary.period }}</span>
        </span>
        <span v-if="summary.note" class="save-bar__summary-note" data-compact="hide">{{ summary.note }}</span>
      </p>
      <slot v-else name="status">
        <!-- Always rendered, empty in a plain bar's `idle`: a live region has to be in the DOM
             before its content changes for the update to be announced. -->
        <span class="save-bar__status" role="status" aria-live="polite">
          <template v-if="statusText">
            <span class="save-bar__dot" aria-hidden="true" />
            {{ statusText }}
          </template>
        </span>
      </slot>
    </div>
    <div class="save-bar__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .save-bar {
    position: sticky;
    z-index: 9;
    /* The card assumes the window scrolls; the client area scrolls inside .layout-content, so
       the bar cancels that container's bottom padding to reach the viewport's edge. */
    bottom: calc(-1 * var(--bf-content-pad-bottom, 0px));
    margin-top: 24px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 66px;
    background: var(--bf-white);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    box-shadow: var(--bf-shadow-1);
    transition:
      box-shadow var(--bf-dur-base) var(--bf-ease),
      border-color var(--bf-dur-base) var(--bf-ease),
      border-radius var(--bf-dur-base) var(--bf-ease);
  }

  /* Flush against the bottom edge: square off the corners that meet it and lift the bar off the
     content with a stronger shadow. */
  .save-bar[data-docked] {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow: var(--bf-shadow-2);
  }

  /* Live edge — by state (unsaved changes), by contract (--live) or by structure (a selection
     summary). One rule for all three. */
  .save-bar[data-state='dirty'],
  .save-bar--live,
  .save-bar:has(> .save-bar__info > .save-bar__summary) {
    border-top-color: var(--bf-orange-dark);
    box-shadow: var(--bf-shadow-2);
  }

  /* __info takes the free width so a long status wraps inside it rather than pushing the
     actions out of the bar. */
  .save-bar__info {
    flex: 1 1 auto;
    min-width: 0;
  }

  .save-bar__status {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-medium);
    color: var(--bf-deep-blue);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  /* The confirmation reads as a whole, so the copy carries the same green as its dot. */
  .save-bar[data-state='saved'] .save-bar__status {
    color: var(--bf-success);
  }

  .save-bar__dot {
    width: 7px;
    height: 7px;
    flex-shrink: 0;
    border-radius: var(--bf-r-pill);
    background: var(--bf-dusky-2);
    transition:
      background var(--bf-dur-base) var(--bf-ease),
      box-shadow var(--bf-dur-base) var(--bf-ease);
  }

  .save-bar[data-state='dirty'] .save-bar__dot {
    background: var(--bf-orange-dark);
    box-shadow: 0 0 0 3px rgba(234, 96, 10, 0.18);
  }

  .save-bar[data-state='saved'] .save-bar__dot {
    background: var(--bf-success);
    box-shadow: 0 0 0 3px rgba(1, 179, 87, 0.18);
  }

  .save-bar__actions {
    display: flex;
    gap: 10px;
  }

  /* Two columns: the name over its qualifier taking the free width, the price over its note
     flush against the actions, so the amount reads as the label of the button beside it. */
  .save-bar__summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'title  price'
      'detail note';
    column-gap: 16px;
    row-gap: 2px;
    align-items: baseline;
    margin: 0;
    min-width: 0;
  }

  .save-bar__summary-title {
    grid-area: title;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    color: var(--bf-fg-1);
  }

  .save-bar__summary-detail {
    grid-area: detail;
    min-width: 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
    overflow-wrap: anywhere;
  }

  .save-bar__summary-price {
    grid-area: price;
    justify-self: end;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    white-space: nowrap;
  }

  /* Without a note the price owns the right column and centres on the title + detail stack. */
  .save-bar__summary:not(:has(> .save-bar__summary-note)) .save-bar__summary-price {
    grid-row: 1 / span 2;
    align-self: center;
  }

  .save-bar__summary-currency {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    letter-spacing: var(--bf-tr-wide);
    color: var(--bf-fg-2);
  }

  .save-bar__summary-amount {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-lg);
    font-weight: var(--bf-fw-extrabold);
    line-height: 1;
    letter-spacing: var(--bf-tr-tight);
    font-variant-numeric: tabular-nums;
    color: var(--bf-fg-1);
  }

  .save-bar__summary-period {
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    color: var(--bf-fg-2);
  }

  /* --bf-fg-2, not the card's --text-tertiary: tertiary is 2.8:1 on white. */
  .save-bar__summary-note {
    grid-area: note;
    justify-self: end;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    color: var(--bf-fg-2);
  }

  @media (max-width: 640px) {
    /* The bar keeps its row at compact width: the status is what the action acts on. */
    .save-bar__actions {
      flex-shrink: 0;
    }

    /* It stacks only when the actions slot holds more than one button. Status above; the pair
       goes column-reverse, full width, so the primary is on top while DOM and focus order stay
       primary-last. */
    .save-bar:has(> .save-bar__actions > :nth-child(2)) {
      flex-direction: column;
      align-items: stretch;
    }

    .save-bar:has(> .save-bar__actions > :nth-child(2)) .save-bar__actions {
      flex-direction: column-reverse;
      flex-shrink: 1;
    }

    .save-bar:has(> .save-bar__actions > :nth-child(2)) .save-bar__actions > :deep(.button) {
      width: 100%;
    }

    /* A state report beside an action, one step down so the dirty copy stays on one line. */
    .save-bar__status {
      font-size: var(--bf-fs-xs);
    }

    /* The parts that restate the chosen option leave the bar; display: none, so a screen reader
       hears what the screen shows. The summary folds to one column, title over price. */
    .save-bar__summary > [data-compact='hide'] {
      display: none;
    }

    .save-bar__summary {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'title'
        'price';
      row-gap: 4px;
    }

    .save-bar .save-bar__summary .save-bar__summary-price {
      grid-area: price;
      grid-row: auto;
      align-self: baseline;
      justify-self: start;
    }
  }
</style>
